import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  CircularProgress,
  Alert,
  Chip,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  TablePagination,
  TableSortLabel,
  Snackbar,
  Menu,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Upload as UploadIcon,
  Download as DownloadIcon,
  GetApp as GetAppIcon,
  CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';
import { devicesAPI } from '../services/api';
import ImportProgressDialog from '../components/ImportProgressDialog';

function DevicesPage() {
  const [devices, setDevices] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingDevice, setEditingDevice] = useState(null);
  const [orderBy, setOrderBy] = useState('imei');
  const [order, setOrder] = useState('asc');
  const [importAnchorEl, setImportAnchorEl] = useState(null);
  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const [templateAnchorEl, setTemplateAnchorEl] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importJobId, setImportJobId] = useState(null);
  const [formData, setFormData] = useState({
    imei: '',
    brand: '',
    model: '',
    installation_location: '',
    status: 'stock',
    notes: '',
  });

  useEffect(() => {
    loadDevices();
  }, [page, rowsPerPage, searchTerm, statusFilter, order, orderBy]);

  const loadDevices = async () => {
    try {
      setLoading(true);
      const params = { 
        page: page + 1, 
        page_size: rowsPerPage 
      };
      
      // Add search filter
      if (searchTerm) {
        params.search = searchTerm;
      }
      
      // Add status filter
      if (statusFilter && statusFilter !== 'all') {
        params.status = statusFilter;
      }
      
      // Add ordering
      if (orderBy) {
        params.ordering = order === 'desc' ? `-${orderBy}` : orderBy;
      }
      
      const response = await devicesAPI.getAll(params);
      // Handle paginated response
      if (response.data.results) {
        setDevices(response.data.results);
        setTotalCount(response.data.count);
      } else {
        setDevices(response.data);
        setTotalCount(response.data.length);
      }
      setError(null);
    } catch (err) {
      console.error('Error loading devices:', err);
      setError('Failed to load devices. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (device = null) => {
    if (device) {
      setEditingDevice(device);
      setFormData({
        imei: device.imei,
        brand: device.brand || '',
        model: device.model || '',
        installation_location: device.installation_location || '',
        status: device.status,
        notes: device.notes || '',
      });
    } else {
      setEditingDevice(null);
      setFormData({
        imei: '',
        brand: '',
        model: '',
        installation_location: '',
        status: 'stock',
        notes: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingDevice(null);
  };

  const handleSubmit = async () => {
    try {
      if (editingDevice) {
        await devicesAPI.update(editingDevice.id, formData);
        showSnackbar('Dispositif mis à jour avec succès!', 'success');
      } else {
        await devicesAPI.create(formData);
        showSnackbar('Dispositif créé avec succès!', 'success');
      }
      handleCloseDialog();
      loadDevices();
    } catch (err) {
      console.error('Error saving device:', err);
      showSnackbar('Erreur: ' + (err.response?.data?.message || err.message), 'error');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce dispositif?')) {
      try {
        await devicesAPI.delete(id);
        showSnackbar('Dispositif supprimé avec succès!', 'success');
        loadDevices();
      } catch (err) {
        console.error('Error deleting device:', err);
        showSnackbar('Erreur lors de la suppression', 'error');
      }
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getStatusColor = (status) => {
    const colors = {
      stock: 'success',
      installed: 'primary',
      maintenance: 'warning',
      inactive: 'default',
    };
    return colors[status] || 'default';
  };

  // Template Download
  const handleDownloadTemplate = async (format) => {
    try {
      const response = await devicesAPI.downloadTemplate(format);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `devices_template.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      showSnackbar(`Template téléchargé (${format.toUpperCase()})`, 'success');
    } catch (err) {
      console.error('Error downloading template:', err);
      showSnackbar('Erreur lors du téléchargement', 'error');
    }
    setTemplateAnchorEl(null);
  };

  // Import Data with Progress
  const handleImportFile = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const response = await devicesAPI.importData(file);
      const { job_id, total_rows } = response.data;
      
      setImportJobId(job_id);
      setImportDialogOpen(true);
      showSnackbar(`Import démarré: ${total_rows} lignes à traiter`, 'info');
    } catch (err) {
      console.error('Error starting import:', err);
      showSnackbar('Erreur import: ' + (err.response?.data?.error || err.message), 'error');
    }
    setImportAnchorEl(null);
    event.target.value = '';
  };

  const handleImportComplete = (jobData) => {
    const { created_count, updated_count, errors } = jobData;
    let message = `Import terminé: ${created_count} créés, ${updated_count} mis à jour`;
    if (errors && errors.length > 0) {
      message += `. ${errors.length} erreurs.`;
    }
    showSnackbar(message, errors?.length > 0 ? 'warning' : 'success');
    loadDevices();
  };

  const handleImportDialogClose = () => {
    setImportDialogOpen(false);
    setImportJobId(null);
  };

  // Export Data
  const handleExport = async (format) => {
    try {
      const response = await devicesAPI.export(format);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `devices_export.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      showSnackbar(`Données exportées (${format.toUpperCase()})`, 'success');
    } catch (err) {
      console.error('Error exporting data:', err);
      showSnackbar('Erreur lors de l\'export', 'error');
    }
    setExportAnchorEl(null);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setPage(0);
  };

  const getComparator = (order, orderBy) => {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  };

  const descendingComparator = (a, b, orderBy) => {
    let aValue = a[orderBy];
    let bValue = b[orderBy];
    
    if (aValue === null || aValue === undefined) aValue = '';
    if (bValue === null || bValue === undefined) bValue = '';
    
    if (typeof aValue === 'string') aValue = aValue.toLowerCase();
    if (typeof bValue === 'string') bValue = bValue.toLowerCase();
    
    if (bValue < aValue) return -1;
    if (bValue > aValue) return 1;
    return 0;
  };

  // Display devices directly - backend handles pagination
  const displayedDevices = devices;

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading) {
    return (
      <Container maxWidth={false} sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth={false} sx={{ mt: 4, mb: 4, px: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Dispositifs GPS
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadDevices}
          >
            Actualiser
          </Button>
          <Button
            variant="outlined"
            startIcon={<GetAppIcon />}
            onClick={(e) => setTemplateAnchorEl(e.currentTarget)}
          >
            Template
          </Button>
          <Button
            variant="outlined"
            startIcon={<UploadIcon />}
            onClick={(e) => setImportAnchorEl(e.currentTarget)}
          >
            Importer
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={(e) => setExportAnchorEl(e.currentTarget)}
          >
            Exporter
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Nouveau Dispositif
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
        <TextField
          fullWidth
          placeholder="Rechercher par IMEI, modèle, SIM ou opérateur..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setPage(0);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Statut</InputLabel>
          <Select
            value={statusFilter}
            label="Statut"
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(0);
            }}
          >
            <MenuItem value="all">Tous</MenuItem>
            <MenuItem value="stock">En Stock</MenuItem>
            <MenuItem value="installed">Installé</MenuItem>
            <MenuItem value="maintenance">Maintenance</MenuItem>
            <MenuItem value="inactive">Inactif</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'imei'}
                  direction={orderBy === 'imei' ? order : 'asc'}
                  onClick={() => handleRequestSort('imei')}
                >
                  <strong>IMEI</strong>
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'model'}
                  direction={orderBy === 'model' ? order : 'asc'}
                  onClick={() => handleRequestSort('model')}
                >
                  <strong>Modèle</strong>
                </TableSortLabel>
              </TableCell>
              <TableCell><strong>SIMs</strong></TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'status'}
                  direction={orderBy === 'status' ? order : 'asc'}
                  onClick={() => handleRequestSort('status')}
                >
                  <strong>Statut</strong>
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'is_synced'}
                  direction={orderBy === 'is_synced' ? order : 'asc'}
                  onClick={() => handleRequestSort('is_synced')}
                >
                  <strong>Synchronisé</strong>
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'server_name'}
                  direction={orderBy === 'server_name' ? order : 'asc'}
                  onClick={() => handleRequestSort('server_name')}
                >
                  <strong>Serveur</strong>
                </TableSortLabel>
              </TableCell>
              <TableCell><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayedDevices.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary">
                    Aucun dispositif trouvé
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              displayedDevices.map((device) => (
                <TableRow key={device.id} hover>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{device.imei}</TableCell>
                  <TableCell>{device.model || '-'}</TableCell>
                  <TableCell>
                    {device.sims_count > 0 ? (
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        <Chip 
                          label={`${device.sims_count} SIM${device.sims_count > 1 ? 's' : ''}`} 
                          size="small" 
                          color="info"
                        />
                        {device.sims_list && device.sims_list.slice(0, 2).map((sim, idx) => (
                          <Chip 
                            key={idx}
                            label={sim.sim_number}
                            size="small"
                            variant="outlined"
                          />
                        ))}
                        {device.sims_count > 2 && (
                          <Chip label={`+${device.sims_count - 2}`} size="small" variant="outlined" />
                        )}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">Aucune</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={device.status_display || device.status}
                      color={getStatusColor(device.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={device.is_synced ? 'Oui' : 'Non'}
                      color={device.is_synced ? 'success' : 'default'}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    {device.server_name ? (
                      <Chip
                        label={device.server_name}
                        size="small"
                        color="info"
                        variant="outlined"
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Modifier">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleOpenDialog(device)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Supprimer">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(device.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50]}
        labelRowsPerPage="Lignes par page:"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
      />

      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 2 }}>
        <Chip label={`En Stock: ${devices.filter(d => d.status === 'stock').length}`} color="success" size="small" />
        <Chip label={`Installés: ${devices.filter(d => d.status === 'installed').length}`} color="primary" size="small" />
        <Chip label={`Maintenance: ${devices.filter(d => d.status === 'maintenance').length}`} color="warning" size="small" />
      </Box>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingDevice ? 'Modifier le dispositif' : 'Nouveau dispositif'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="IMEI *"
              value={formData.imei}
              onChange={(e) => setFormData({ ...formData, imei: e.target.value })}
              fullWidth
              required
              helperText="15 chiffres"
              inputProps={{ maxLength: 15 }}
            />

            <TextField
              label="Marque"
              value={formData.brand}
              onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
              fullWidth
            />

            <TextField
              label="Modèle *"
              value={formData.model}
              onChange={(e) => setFormData({ ...formData, model: e.target.value })}
              fullWidth
              required
            />

            <TextField
              label="Emplacement d'installation"
              value={formData.installation_location}
              onChange={(e) => setFormData({ ...formData, installation_location: e.target.value })}
              fullWidth
              helperText="Ex: sous tableau de bord, coffre, etc."
            />

            <FormControl fullWidth required>
              <InputLabel>Statut</InputLabel>
              <Select
                value={formData.status}
                label="Statut"
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              >
                <MenuItem value="stock">En Stock</MenuItem>
                <MenuItem value="installed">Installé</MenuItem>
                <MenuItem value="maintenance">Maintenance</MenuItem>
                <MenuItem value="inactive">Inactif</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              fullWidth
              multiline
              rows={3}
              helperText="Informations supplémentaires sur ce dispositif"
            />

            <Alert severity="info" sx={{ mt: 1 }}>
              Les cartes SIM peuvent être assignées depuis la page "Cartes SIM"
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Annuler</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.imei || !formData.model}
          >
            {editingDevice ? 'Mettre à jour' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Template Download Menu */}
      <Menu
        anchorEl={templateAnchorEl}
        open={Boolean(templateAnchorEl)}
        onClose={() => setTemplateAnchorEl(null)}
      >
        <MenuItem onClick={() => handleDownloadTemplate('xlsx')}>
          <CloudUploadIcon sx={{ mr: 1 }} /> Excel (.xlsx)
        </MenuItem>
        <MenuItem onClick={() => handleDownloadTemplate('csv')}>
          <CloudUploadIcon sx={{ mr: 1 }} /> CSV (.csv)
        </MenuItem>
        <MenuItem onClick={() => handleDownloadTemplate('json')}>
          <CloudUploadIcon sx={{ mr: 1 }} /> JSON (.json)
        </MenuItem>
      </Menu>

      {/* Import Menu */}
      <Menu
        anchorEl={importAnchorEl}
        open={Boolean(importAnchorEl)}
        onClose={() => setImportAnchorEl(null)}
      >
        <MenuItem component="label">
          <UploadIcon sx={{ mr: 1 }} /> Excel (.xlsx)
          <input
            type="file"
            hidden
            accept=".xlsx,.xls"
            onChange={handleImportFile}
          />
        </MenuItem>
        <MenuItem component="label">
          <UploadIcon sx={{ mr: 1 }} /> CSV (.csv)
          <input
            type="file"
            hidden
            accept=".csv"
            onChange={handleImportFile}
          />
        </MenuItem>
        <MenuItem component="label">
          <UploadIcon sx={{ mr: 1 }} /> JSON (.json)
          <input
            type="file"
            hidden
            accept=".json"
            onChange={handleImportFile}
          />
        </MenuItem>
      </Menu>

      {/* Export Menu */}
      <Menu
        anchorEl={exportAnchorEl}
        open={Boolean(exportAnchorEl)}
        onClose={() => setExportAnchorEl(null)}
      >
        <MenuItem onClick={() => handleExport('xlsx')}>
          <DownloadIcon sx={{ mr: 1 }} /> Excel (.xlsx)
        </MenuItem>
        <MenuItem onClick={() => handleExport('csv')}>
          <DownloadIcon sx={{ mr: 1 }} /> CSV (.csv)
        </MenuItem>
        <MenuItem onClick={() => handleExport('json')}>
          <DownloadIcon sx={{ mr: 1 }} /> JSON (.json)
        </MenuItem>
      </Menu>

      {/* Import Progress Dialog */}
      <ImportProgressDialog
        open={importDialogOpen}
        jobId={importJobId}
        getStatusFn={devicesAPI.getImportStatus}
        onComplete={handleImportComplete}
        onClose={handleImportDialogClose}
        title="Import des dispositifs..."
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default DevicesPage;
